import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import KeyboardRoundedIcon from '@mui/icons-material/KeyboardRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import TokenRoundedIcon from '@mui/icons-material/TokenRounded';
import VideocamRoundedIcon from '@mui/icons-material/VideocamRounded';
import type { ProUnlockCounts } from 'src/openapi';

/**
 * The store API's Pro figures. Widened because the generated client predates
 * `early_access_jobs`: live external postings still inside the early-access
 * window, which free candidates do not see in Find Jobs yet.
 */
export type ProUnlocks = ProUnlockCounts & { early_access_jobs?: number };

/**
 * Rupee value of the credits the pass grants, priced the way the backend's own
 * Pro+ comparison prices them (build_premium_promo_savings):
 *
 *   2500 AI Guide interactions ÷ 100 per unlock = 25 × 25 credits = 625
 *
 * At PREMIUM_PROMO_RUPEES_PER_CREDIT = 1 that is ₹625. The pass grants no
 * general credits (PREMIUM_STANDARD_GRANT_CREDITS = 0); AI interactions are
 * their own unit, so this is a rate, not 2,500 × a per-credit price.
 */
export const PRO_GRANT_RUPEE_VALUE = 625;

/**
 * What every Pro+ month grants, the free period included — mirrors
 * PREMIUM_STANDARD_GRANT_AI_INTERACTIONS, which the backend re-grants on each
 * paid cycle. The pass carries no general credits.
 */
export const PRO_GRANT_AI_CREDITS = 2500;

/**
 * A month's Pro+ AI credits are that month's allowance: what is left expires
 * when the month ends or the next batch arrives. Credits bought or earned
 * elsewhere never expire, and Pro+ credits are always spent first.
 */
export const PRO_AI_CREDITS_EXPIRY_NOTE = 'Unused AI credits expire monthly';

// Spotlight acts on Abekus-posted jobs only: the perks are recruiter-side
// (dashboard ranking, DMs, verified emails), and aggregated external listings
// have no Abekus recruiter to reach. Stated on the card, the perks modal, the
// mobile sheet and every Pro list with identical wording. Lives here, not in
// the store's constants, so the plan below can quote it without an import
// cycle; the store re-exports it.
export const SPOTLIGHT_SCOPE_NOTE =
  'Spotlight works for jobs managed by Abekus only — not external/aggregated listings.';

/**
 * The whole Pro plan, once. Every surface that pitches Pro lists these eight —
 * as chips, rows, tiles or sentences — so a change to the plan is one edit
 * here, and TypeScript flags a surface keyed on the plan that misses one.
 *
 * Kept honest against the backend's PREMIUM_STANDARD_COVERED_ACTIONS and the
 * one-time grants:
 *  - AI credits are metered on every plan; Pro grants them, and never makes an
 *    AI feature free or unlimited -- except job-form autofill, whose AI
 *    answers the plan waives (AutofillAiAnswer is a covered action).
 *  - The pass grants no general credits.
 *  - Spotlight is not sold as part of Pro (for now): it appears on no Pro
 *    surface, though the plan still grants it.
 *  - Applying is free for everyone; Pro only waives the credits that
 *    switching on auto-apply costs.
 *  - One guidance call a month is free; calls beyond it are paid.
 *  - Hidden jobs leave the free Find Jobs list for a while; they aren't locked.
 */
export type ProPlanKey =
  | 'courses'
  | 'ai'
  | 'autofill'
  | 'hidden'
  | 'resume'
  | 'apply'
  | 'competitions'
  | 'guidance';

export interface ProPlanItem {
  key: ProPlanKey;
  icon: typeof SchoolRoundedIcon;
  /** A few words: chips, table cells, inline lists. */
  short: string;
  /** One sentence, for lists with room to explain. */
  long: string;
  /** A caveat the buyer needs before paying, shown wherever there is room. */
  scope?: string;
}

/** The plan with the store's live counts filled in where it has them, and a
 * tier's own AI-credit grant (Pro+'s by default). */
export const proPlan = (
  counts?: ProUnlocks,
  aiCredits: number = PRO_GRANT_AI_CREDITS,
): ProPlanItem[] => {
  const courses = counts?.paid_courses;
  const hidden = counts?.early_access_jobs;
  const ai = aiCredits.toLocaleString('en-IN');
  return [
    {
      key: 'courses',
      icon: SchoolRoundedIcon,
      short: courses
        ? `All ${courses.toLocaleString('en-IN')} paid courses`
        : 'Every paid course',
      long: `${courses ? `All ${courses.toLocaleString('en-IN')}` : 'Every'} paid course${courses === 1 ? '' : 's'} unlocked — no credits, no per-course unlock.`,
    },
    {
      key: 'ai',
      icon: TokenRoundedIcon,
      short: `${ai} AI credits`,
      long: `${ai} fresh AI credits every Pro month, for tailored resumes, cover letters, resume scoring and the AI Guide. ${PRO_AI_CREDITS_EXPIRY_NOTE} — they don't carry over.`,
    },
    {
      key: 'autofill',
      icon: KeyboardRoundedIcon,
      short: 'Unlimited job-form autofill',
      long: 'Unlimited autofill on job application forms with the Abekus Chrome extension, AI-written answers included, with no credits.',
    },
    {
      key: 'hidden',
      icon: BoltRoundedIcon,
      short: hidden
        ? `${hidden.toLocaleString('en-IN')} hidden jobs`
        : 'Hidden jobs',
      long: 'Every hidden job unlocked, ready to apply to.',
    },
    {
      key: 'resume',
      icon: DescriptionRoundedIcon,
      short: 'Every Pro resume template',
      long: 'Every Pro resume template, plus resume parsing, snippet rewrites and re-evaluation without credits.',
    },
    {
      key: 'apply',
      icon: SendRoundedIcon,
      short: 'Auto-apply, no credits',
      long: 'Switch on auto-apply without spending credits.',
    },
    {
      key: 'competitions',
      icon: EmojiEventsRoundedIcon,
      short: 'Competition entries, no credits',
      long: 'Competition and hiring-event entries without credits.',
    },
    {
      key: 'guidance',
      icon: VideocamRoundedIcon,
      short: '1:1 career call free every month',
      long: 'A 1:1 career guidance call every month, free.',
    },
  ];
};

/** The plan without live counts, for copy that is built before they load. */
export const PRO_PLAN: ProPlanItem[] = proPlan();

/**
 * What the pass grants, as a value each: a figure a buyer can weigh, not a
 * sentence they have to read. The tiles are the whole plan — the pitch used to
 * spell each one out in a label plus a detail line, and a stack of those is a
 * wall of prose nobody finishes on a paywall.
 *
 * `value` carries the weight, so it has to be a real figure ("2,500", "5×")
 * and never a word that could have been the label. One tile per PRO_PLAN item
 * (enforced by TILES below), with the plan's icon, so the grid and every other
 * list stay the same plan.
 */
export interface ProValueTile {
  key: ProPlanKey;
  icon: typeof SchoolRoundedIcon;
  value: string;
  label: string;
  /** The caption under the figure — a few words, never a sentence. */
  hint: string;
  isNew?: boolean;
  /**
   * Which server-side count fills this tile's value, if any. Substituted by
   * name rather than by position so reordering the grid can't silently move a
   * figure onto the wrong benefit.
   */
  countKey?: keyof ProUnlocks;
  /** Label and caption once the server's figure replaces `value`. */
  counted?: { label: string; hint: string };
}

/**
 * The fallback grid: what the tiles say before the counts arrive, and on a
 * backend old enough not to send them. The countable tiles read as words here
 * ("ALL", "NEW") — true, just not checkable.
 *
 * A Record so a plan item without a tile fails the type check instead of
 * quietly dropping off the paywall. No practice tile: Skill Builder practice
 * is off the product, and a benefit nobody can reach sells nothing.
 */
const TILES: Record<ProPlanKey, Omit<ProValueTile, 'key' | 'icon'>> = {
  courses: {
    value: 'ALL',
    label: 'Courses',
    hint: 'Every paid course, unlocked',
    countKey: 'paid_courses',
  },
  ai: {
    value: PRO_GRANT_AI_CREDITS.toLocaleString('en-IN'),
    label: `AI credits worth ₹${PRO_GRANT_RUPEE_VALUE}`,
    // Not "spend anywhere": AI credits sit on their own balance, apart from
    // general credits. Resume scoring leads: it is what learners spend them on
    // most.
    hint: 'Resume scoring, tailoring, cover letters',
  },
  autofill: {
    value: '∞',
    label: 'Job-form autofills',
    // Learners without Pro buy AI-answer top-ups; on Pro there are none to buy.
    hint: 'AI answers included, no top-ups',
  },
  hidden: {
    value: 'NEW',
    label: 'Hidden jobs',
    hint: 'Unlocked with Pro',
    isNew: true,
    countKey: 'early_access_jobs',
    counted: {
      label: 'Hidden jobs now',
      hint: 'Unlocked with Pro',
    },
  },
  resume: {
    value: 'PRO',
    label: 'Resume templates',
    // Scoring and tailoring are AI-credit spends, so they are not claimed here.
    hint: 'Plus parsing and snippet rewrites',
  },
  apply: {
    value: '0',
    label: 'credits for auto-apply',
    // Applying itself is free for everyone; switching auto-apply on is not.
    hint: 'Switching it on costs credits without Pro',
  },
  competitions: {
    value: '0',
    label: 'credits to compete',
    hint: 'Competitions, hiring events',
  },
  guidance: {
    value: 'Monthly',
    label: '1:1 career call free',
    // The call's list price; it sells for less during the launch promotion.
    hint: 'A 30-minute mentor session, worth ₹500',
  },
};

export const PRO_VALUE_TILES: ProValueTile[] = PRO_PLAN.map(
  ({ key, icon }) => ({ key, icon, ...TILES[key] }),
);

/**
 * How many benefits the pitch lists. Anything that teases Pro elsewhere ("+N
 * more") counts from here, so a tease can never promise more than this screen
 * shows.
 */
export const PRO_BENEFIT_COUNT = PRO_VALUE_TILES.length;

/** A tier's own grant, priced at Pro+'s rate. */
const aiTileFor = (aiCredits: number) => ({
  value: aiCredits.toLocaleString('en-IN'),
  label: `AI credits worth ₹${Math.round(
    (aiCredits * PRO_GRANT_RUPEE_VALUE) / PRO_GRANT_AI_CREDITS,
  )}`,
});

/**
 * Plan items the grid leaves out: most competitions and hiring events cost
 * nothing to enter for anyone, so "0 credits to compete" promised a saving on
 * something already free.
 */
const STORE_HIDDEN_TILES: ProPlanKey[] = ['competitions'];

/**
 * The grid with the server's figures filled in.
 *
 * "ALL courses" was a word formatted as a value, on a screen whose whole
 * premise is that a buyer weighs figures instead of reading sentences.
 *
 * A zero keeps the wording the tile had rather than announcing "0 courses" on a
 * paywall. A tier other than Pro+ gets its own AI grant and no call tile.
 */
export const proValueTiles = (
  counts?: ProUnlocks,
  tier: { aiCredits: number; monthlyCall: boolean } = {
    aiCredits: PRO_GRANT_AI_CREDITS,
    monthlyCall: true,
  },
): ProValueTile[] =>
  PRO_VALUE_TILES.filter(
    (tile) =>
      !STORE_HIDDEN_TILES.includes(tile.key) &&
      (tile.key !== 'guidance' || tier.monthlyCall),
  ).map((tile) => {
    if (tile.key === 'ai') return { ...tile, ...aiTileFor(tier.aiCredits) };
    const count = tile.countKey ? counts?.[tile.countKey] : undefined;
    if (!count) return tile;
    return {
      ...tile,
      ...tile.counted,
      value: count.toLocaleString('en-IN'),
    };
  });

/**
 * Rupee value of the career guidance call the pass includes each month — a
 * 30-minute 1:1 that otherwise costs this much (credits convert 1:1 with rupees).
 *
 * Only ever compared against the pass price, never printed. The real figure is
 * CREDIT_VALUES["CareerGuidanceCall"], which an admin retunes live from the
 * credit-pricing page and this dialog has no way to read — it is handed a store
 * item, not a guidance quote. So quoting it here would go stale silently, while
 * comparing against it degrades safely: too low and the bar simply doesn't
 * claim the pass pays for itself.
 */
export const PRO_INCLUDED_GUIDANCE_VALUE = 500;
